@file:Suppress("TooGenericExceptionThrown")

package com.klantenstop.web.data.repos

import com.klantenstop.web.data.NetworkingConfig
import com.klantenstop.web.data.deleteRequest
import com.klantenstop.web.data.getRequest
import com.klantenstop.web.data.models.NetworkResponse
import com.klantenstop.web.data.models.domain.Review
import com.klantenstop.web.data.models.domain.ReviewTotal
import com.klantenstop.web.data.models.domain.toReview
import com.klantenstop.web.data.models.domain.toReviewTotal
import com.klantenstop.web.data.models.dto.requests.reviews.InsertReview
import com.klantenstop.web.data.models.dto.requests.reviews.UpdateReview
import com.klantenstop.web.data.models.dto.responses.ErrorResponse
import com.klantenstop.web.data.models.dto.responses.ReviewDto
import com.klantenstop.web.data.models.dto.responses.ReviewTotalDto
import com.klantenstop.web.data.postRequest
import com.klantenstop.web.data.putRequest

object ReviewRepo {
    private val companyUrl = "${NetworkingConfig.baseUrl}/api/v1/companies"
    private fun reviewsPerCompanyUrl(companyId: String) = "${companyUrl}/$companyId/reviews"
    private val reviewsUrl = "${NetworkingConfig.baseUrl}/api/v1/reviews"

    suspend fun createReview(companyId: String, insertReview: InsertReview): Review {
        val response: NetworkResponse<ReviewDto, ErrorResponse> = postRequest(reviewsPerCompanyUrl(companyId), insertReview)

        return when (response) {
            is NetworkResponse.Success -> response.body.toReview()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het aanmaken van review"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het aanmaken van review")
        }
    }

    suspend fun getReviewsByCompanyId(companyId: String): List<Review> {
        val response: NetworkResponse<List<ReviewDto>, ErrorResponse> = getRequest(reviewsPerCompanyUrl(companyId))

        return when (response) {
            is NetworkResponse.Success -> response.body.map { it.toReview() }
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het ophalen van reviews"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het ophalen van reviews")
        }
    }

    suspend fun getMyReviewByCompanyId(companyId: String): Review {
        val response: NetworkResponse<ReviewDto, ErrorResponse> = getRequest(reviewsPerCompanyUrl(companyId) + "/current-user")

        return when (response) {
            is NetworkResponse.Success -> response.body.toReview()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het ophalen van jouw review"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het ophalen van jouw review")
        }
    }

    suspend fun getReviewTotal(companyId: String): ReviewTotal {
        val response: NetworkResponse<ReviewTotalDto, ErrorResponse> = getRequest(reviewsPerCompanyUrl(companyId) + "/total")

        return when (response) {
            is NetworkResponse.Success -> response.body.toReviewTotal()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het ophalen van review totaal"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het ophalen van review totaal")
        }
    }

    suspend fun updateReview(reviewId: String, updateReview: UpdateReview): Review {
        val response: NetworkResponse<ReviewDto, ErrorResponse> = putRequest("$reviewsUrl/$reviewId", updateReview)

        return when (response) {
            is NetworkResponse.Success -> response.body.toReview()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het bijwerken van review"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het bijwerken van review")
        }
    }

    suspend fun deleteReview(reviewId: String): Boolean {
        val response: NetworkResponse<Unit, ErrorResponse> = deleteRequest("$reviewsUrl/$reviewId")

        return when (response) {
            is NetworkResponse.Success -> true
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: response.body?.error ?: "Server fout bij het verwijderen van review"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het verwijderen van review")
        }
    }
}
